import { Injectable, inject } from '@angular/core'
import { LDClient, LDUser } from 'launchdarkly-js-client-sdk'
import { LDFlagValue } from 'launchdarkly-js-sdk-common'
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { filter, switchMap, take } from 'rxjs/operators'
import {
  FeatureFlagConfiguration,
  FEATURE_FLAG_CONFIG,
  FEATURE_FLAG_FACTORY,
} from './feature-flag.core'

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  /** Max time we wait for LaunchDarkly before serving default flag values.
   * Corporate proxies/ad-blockers can block LD entirely (requests fail with
   * status 0); without this the app would wait forever and render nothing. */
  private static readonly READY_TIMEOUT_MS = 5000

  protected config = inject<FeatureFlagConfiguration>(FEATURE_FLAG_CONFIG)

  protected readonly _client = inject<LDClient>(FEATURE_FLAG_FACTORY)

  protected readonly _user$ = new Subject<LDUser>()

  protected readonly _ready$ = new BehaviorSubject<boolean>(false)

  protected readonly _flags$ = new Map()

  protected get enabled(): boolean {
    return !!this._client
  }

  public init(): void {
    if (!this._client) {
      return
    }
    this._client.on('ready', () => {
      this._ready$.next(true)
    })
    this._client.on('failed', () => {
      this._ready$.next(true)
    })
    setTimeout(() => {
      this._ready$.next(true)
    }, FeatureFlagService.READY_TIMEOUT_MS)
    this._user$.subscribe(async (user) => this.loadUser(user))
  }

  public setUser(user: LDUser): void {
    this._user$.next(user)
  }

  public watch<T extends LDFlagValue>(
    flag: string,
    defaultValue: T
  ): Observable<T> {
    if (!this.enabled) {
      return of(defaultValue)
    }
    return this._ready$.pipe(
      switchMap(() => this.getFlag$<T>(flag, defaultValue))
    )
  }

  protected getFlag$<T>(flag: string, defaultValue: unknown): Observable<T> {
    if (!this._flags$.has(flag)) {
      const flag$ = new ReplaySubject<T>(1)
      this._flags$.set(flag, flag$)

      // We wait to be ready
      this._ready$
        .pipe(
          filter((ready) => ready),
          take(1)
        )
        .subscribe(() => {
          // Then we get a the value of the flag
          const init = this._client.variation(flag, defaultValue)
          flag$.next(init as T)

          // And we listen to changes
          this._client.on(`change:${flag}`, (value) => {
            flag$.next(value as T)
          })
        })
      return flag$
    }
    return this._flags$.get(flag)
  }

  protected async loadUser(user: LDUser): Promise<void> {
    if (!this.enabled) {
      return
    }
    if (!user?.key) {
      await this._client.identify({
        key: 'anonymous',
      })
    } else {
      await this._client.identify(user)
    }
  }
}
